import { inject, Injectable } from "@angular/core";
import {
  CreateEventDto,
  CreateVisitDto,
  UpdateEventDto,
  UpdateTestDto,
  UpdateVisitDto,
} from "../../api/models";
import { BookingConsultationState } from "../../interfaces/booking-consultation-state.interface";
import { BookingPrescriptionState } from "../../interfaces/booking-prescription-state.interface";
import { environment } from "../../environments/environment";
import {
  mapBookingConsultationStateToCreateDto,
  mapBookingConsultationStateToUpdateDto,
} from "../../helpers/mappers/booking-consultation-state.mapper";
import { mapBookingPrescriptionStateToUpdateDto } from "../../helpers/mappers/booking-prescription-state.mapper";
import { PathwaysApiService } from "../api";

/**
 * Type definition for available actions on Google Calendar events
 */
export type ActionType = "create" | "update";

/**
 * Type definition for the content type of the event (visit or test)
 */
export type EventContentType = "visit" | "test";

/**
 * Interface for the DTO mapping result containing the mapped DTO and action type
 * @template T - The type of the DTO (CreateEventDto or UpdateEventDto)
 */
export interface DtoMappingResult<T> {
  /** The mapped DTO object */
  dto: T;
  /** The action type that was used for mapping */
  action: ActionType;
}

/**
 * Service responsible for mapping booking consultation state to Google Calendar event DTOs.
 * Handles both creation and update operations for visits and tests.
 */
@Injectable({
  providedIn: "root",
})
export class GoogleEventsMapperService {
  /** Default duration for consultations in minutes */
  private readonly CONSULTATION_DURATION_MINUTES = 30;

  /** The pathways API service instance */
  private readonly pathwayApiService = inject(PathwaysApiService);

  /**
   * Maps booking consultation state to the appropriate Google Calendar event DTO
   * based on the specified action and content type.
   * @param state - The current booking consultation state
   * @param action - The action to perform (create or update)
   * @param type - The type of content (visit or test)
   * @returns An object containing the mapped DTO and action type
   * @throws Error when an unsupported action is provided
   */
  public mapToEventDto(
    state: BookingConsultationState | BookingPrescriptionState,
    action: ActionType,
    type: EventContentType,
  ): DtoMappingResult<CreateEventDto | UpdateEventDto> {
    const baseMapping = this._createBaseMapping(state);

    switch (action) {
      case "create":
        return {
          dto: this._createEventDto(state, baseMapping, type),
          action,
        };
      case "update":
        return {
          dto: this._updateEventDto(state, baseMapping, type),
          action,
        };
      default:
        throw new Error(`Azione non supportata: ${action}`);
    }
  }

  /**
   * Creates the base mapping object containing common properties for all event DTOs.
   * Calculates start and end times, attendees, and other shared properties.
   * @param state - The booking consultation state
   * @returns Base mapping object with common event properties
   */
  private _createBaseMapping(
    state: BookingConsultationState | BookingPrescriptionState,
  ) {
    const startDate = new Date(state.bookingDate!);
    const endDate = new Date(
      startDate.getTime() + this.CONSULTATION_DURATION_MINUTES * 60000,
    );

    return {
      attendees: state.patientEmail ? [state.patientEmail] : [],
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      summary: state.title || "",
      organizerEmail: this._returnOrganizerEmail(state),
      tenant: environment.tenant,
    };
  }

  private _returnOrganizerEmail(
    state: BookingConsultationState | BookingPrescriptionState,
  ): string {
    if ("doctorEmail" in state && state.doctorEmail) {
      return state.doctorEmail;
    }
    if ("clinicEmail" in state && state.clinicEmail) {
      return state.clinicEmail;
    }
    return "";
  }

  /**
   * Creates a Google Calendar event DTO for new events.
   * Combines base mapping with content-specific data based on the event type.
   * @param state - The booking consultation state
   * @param baseMapping - Base mapping object with common properties
   * @param type - The type of content (visit or test)
   * @returns CreateEventDto with all required properties
   */
  private _createEventDto(
    state: BookingConsultationState | BookingPrescriptionState,
    baseMapping: ReturnType<typeof this._createBaseMapping>,
    type: EventContentType,
  ): CreateEventDto {
    const dto: CreateEventDto = {
      attendees: baseMapping.attendees,
      end: baseMapping.end,
      organizerEmail: baseMapping.organizerEmail,
      start: baseMapping.start,
      summary: baseMapping.summary,
      tenant: baseMapping.tenant,
    };

    if (type === "visit") {
      const consultationState = state as BookingConsultationState;
      if (consultationState.visitId) {
        dto.id = consultationState.visitId;
        dto.visit = this._updateVisitDto(consultationState);
      } else {
        dto.visit = this._createVisitDto(consultationState);
      }
    }

    if (type === "test") {
      dto.test = this._updateTestDto(state as BookingPrescriptionState);
      dto.id = (state as BookingPrescriptionState).testId;
    }

    return dto;
  }

  /**
   * Creates a Google Calendar event DTO for updating existing events.
   * Combines base mapping with content-specific data based on the event type.
   * @param state - The booking consultation state
   * @param baseMapping - Base mapping object with common properties
   * @param type - The type of content (visit or test)
   * @returns UpdateEventDto with properties to be updated
   */
  private _updateEventDto(
    state: BookingConsultationState | BookingPrescriptionState,
    baseMapping: ReturnType<typeof this._createBaseMapping>,
    type: EventContentType,
  ): UpdateEventDto {
    const dto: UpdateEventDto = {
      attendees: baseMapping.attendees,
      end: baseMapping.end,
      start: baseMapping.start,
      summary: baseMapping.summary,
    };

    if (type === "visit") {
      dto.visit = this._updateVisitDto(state as BookingConsultationState);
    } else if (type === "test") {
      dto.test = this._updateTestDto(state as BookingPrescriptionState);
    }

    return dto;
  }

  /**
   * Creates a visit DTO for new visit events.
   * Delegates to the existing booking state mapper for visit creation.
   * @param state - The booking consultation state
   * @returns CreateVisitDto with visit-specific data
   */
  private _createVisitDto(state: BookingConsultationState): CreateVisitDto {
    return mapBookingConsultationStateToCreateDto(state);
  }

  /**
   * Creates a visit DTO for updating existing visit events.
   * Delegates to the existing booking state mapper for visit updates.
   * @param state - The booking consultation state
   * @returns UpdateVisitDto with visit-specific data
   */
  private _updateVisitDto(state: BookingConsultationState): UpdateVisitDto {
    return mapBookingConsultationStateToUpdateDto(state);
  }

  /**
   * Creates a test DTO for updating existing test events.
   * @param state - The booking prescription state
   * @returns UpdateTestDto with test-specific data
   * @private
   */
  private _updateTestDto(state: BookingPrescriptionState): UpdateTestDto {
    return mapBookingPrescriptionStateToUpdateDto(state);
  }
}
